import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Building2, Check, Phone, HardHat, MessageCircle, Languages, BarChart3, Users, ShieldAlert, ArrowRight, Boxes } from "lucide-react";
import Seo, { breadcrumbLd } from "@/components/Seo";
import { MarketingNav, MarketingFooter } from "@/components/MarketingChrome";

const INCLUDED = [
  "The AI resident concierge, answers by text, grounded in your documents",
  "The community command center: requests, broadcasts and reporting",
  "Documents, rules, events, amenities and contacts",
  "Resident roster, consent capture and opt-in management",
  "A branded community page and resident portal",
  "Manager dashboard with the work in one place",
];

const ADDONS = [
  { icon: Phone, title: "AI Voice Concierge", body: "A real phone line the AI answers 24/7, grounded in the same documents." },
  { icon: HardHat, title: "Field Services", body: "Vendors, work orders, insurance gating, photo proof and coded invoices." },
  { icon: MessageCircle, title: "WhatsApp", body: "Meet residents on the channel they already use every day." },
  { icon: Languages, title: "Multi-Language", body: "Answer and broadcast in each resident's preferred language." },
  { icon: BarChart3, title: "Portfolio Intelligence", body: "Community Pulse and service patterns across your whole book of business." },
  { icon: Users, title: "Community Concierge Team", body: "Real people who step into a text conversation whenever a resident needs a human." },
  { icon: ShieldAlert, title: "Neighborhood Watch", body: "24/7 live security monitoring, operators can assess and escalate in real time." },
];

export default function PricingPage() {
  const jsonLd = breadcrumbLd([{ name: "Home", path: "/" }, { name: "Pricing", path: "/pricing" }]);
  return (
    <div className="font-body bg-sand-50 text-ink min-h-screen" data-testid="pricing-page">
      <Seo
        title="Pricing, One Plan Per Community, Portfolio Terms That Scale | Ask Redline"
        description="Ask Redline is priced per community and sized to the community, no per-seat fees. Add modules when you want them, with portfolio terms built for HOA management companies. Book a demo for a proposal."
        canonical="/pricing"
        jsonLd={jsonLd}
        scrollTop
      />
      <MarketingNav />

      <section className="max-w-6xl mx-auto px-6 pt-14 pb-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-redline-50 text-redline-700 text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 mb-5">
            <Boxes className="h-3.5 w-3.5" strokeWidth={2} /> Pricing
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08]">Pricing that scales with your portfolio.</h1>
          <p className="mt-5 text-lg text-ink-700 leading-relaxed">
            One plan per community, sized to the community, no per-seat charges and no back-office overhaul. Turn on modules as you
            need them, and grow into portfolio terms built for management companies running many communities.
          </p>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-12 grid lg:grid-cols-2 gap-5">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4 }}
          className="rounded-3xl border border-sand-200 bg-white p-8 sm:p-10" data-testid="pricing-included">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-redline-500">Every community includes</div>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight mt-2">The concierge and the command center</h2>
          <p className="text-sm text-ink-600 mt-3 leading-relaxed">Sold per community, not per user, per admin or per message tier.</p>
          <ul className="mt-6 space-y-3">
            {INCLUDED.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-ink-700 leading-relaxed">
                <span className="mt-0.5 h-5 w-5 rounded-full bg-redline-50 flex items-center justify-center shrink-0"><Check className="h-3.5 w-3.5 text-redline-600" strokeWidth={2.5} /></span>
                {f}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.4, delay: 0.06 }}
          className="rounded-3xl border border-sand-200 bg-white p-8 sm:p-10" data-testid="pricing-addons">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-redline-500">Add-on modules</div>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight mt-2">Layer on more when you want it</h2>
          <p className="text-sm text-ink-600 mt-3 leading-relaxed">Each module is optional and can be enabled per community.</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-5">
            {ADDONS.map((a) => {
              const Icon = a.icon;
              return (
                <div key={a.title} className="flex items-start gap-3">
                  <span className="mt-0.5 h-8 w-8 rounded-lg bg-sand-100 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-ink-700" strokeWidth={1.75} /></span>
                  <div>
                    <div className="text-sm font-bold text-ink">{a.title}</div>
                    <div className="text-xs text-ink-600 mt-1 leading-relaxed">{a.body}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-ink text-white p-10 sm:p-14" data-testid="pricing-enterprise">
          <div className="absolute -left-16 -bottom-16 h-64 w-64 rounded-full bg-redline-500/20 blur-3xl" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 text-sand-200 text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 mb-5">
              <Building2 className="h-3.5 w-3.5" strokeWidth={2} /> For management companies
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight max-w-2xl">Portfolio pricing built for the whole book of business.</h2>
            <p className="mt-4 text-sand-300 leading-relaxed max-w-2xl">
              Running dozens or hundreds of communities? We build a proposal around your portfolio, volume terms, a portfolio
              dashboard and Community Pulse across every property, guided onboarding, and a single point of contact. You keep the
              accounting and management systems you already run.
            </p>
            <div className="mt-8 grid sm:grid-cols-3 gap-4 max-w-2xl">
              {["Volume terms across the portfolio", "Portfolio dashboard & intelligence", "Guided onboarding & support"].map((x) => (
                <div key={x} className="flex items-start gap-2.5 text-sm text-white/90">
                  <Check className="h-4 w-4 text-redline-400 mt-0.5 shrink-0" strokeWidth={2.5} /> {x}
                </div>
              ))}
            </div>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <a href="/#contact" data-testid="pricing-book-demo" className="inline-flex items-center justify-center gap-2 rounded-full bg-redline-500 text-white font-semibold px-7 py-3.5 hover:bg-redline-600 transition-colors">
                Book a demo for a proposal <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/compare" className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 text-white font-semibold px-7 py-3.5 hover:bg-white/15 transition-colors">
                Compare alternatives
              </Link>
            </div>
            <p className="mt-6 text-xs text-sand-400">No per-user or per-seat fees. Pricing is sized to each community, talk to us and we'll put real numbers to your portfolio.</p>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
